import extractErrorMessage from '@lib/utils/extractErrorMessage';

export const RTCS_CONNECTION_LOST_MESSAGE = 'Real time connection lost';

/** Matches connectionStateMonitor poll granularity so short blips stay invisible. */
export const RTCS_RECONNECTING_DEBOUNCE_MS = 5000;

export type RtcsConnectionPhase = 'reconnecting' | 'lost';

/**
 * Set while openWorkspace is retrying after an RTCS outage. closeWorkspace
 * clears error mid-retry; this keeps the navbar in reconnecting until the
 * attempt finishes.
 */
let rtcsRetryInFlight = false;

export function setRtcsRetryInFlight(value: boolean): void {
  rtcsRetryInFlight = value;
}

export function isRtcsConnectionLostError(error: unknown): boolean {
  if (error == null) {
    return false;
  }
  return extractErrorMessage(error) === RTCS_CONNECTION_LOST_MESSAGE;
}

/**
 * Navbar RTCS announcement phase from existing serviceState flags (Decision 2a).
 * Returns null while loading (except a user Retry after outage), when healthy,
 * or for non-RTCS workspace errors (prefetch / initial connect throw) so those
 * keep today's surfaces.
 */
export function getRtcsConnectionPhase({
  loading,
  error,
  serviceConnected,
}: {
  loading: boolean;
  error: unknown;
  serviceConnected: boolean;
}): RtcsConnectionPhase | null {
  if (loading) {
    if (rtcsRetryInFlight || isRtcsConnectionLostError(error)) {
      return 'reconnecting';
    }
    return null;
  }
  if (isRtcsConnectionLostError(error)) {
    return 'lost';
  }
  if (!serviceConnected && error == null) {
    return 'reconnecting';
  }
  return null;
}

export function isRtcsOutageActive({
  flagEnabled,
  loading,
  error,
  serviceConnected,
}: {
  flagEnabled: boolean;
  loading: boolean;
  error: unknown;
  serviceConnected: boolean;
}): boolean {
  if (!flagEnabled) {
    return false;
  }
  return getRtcsConnectionPhase({ loading, error, serviceConnected }) != null;
}
